import { RequestError } from 'hooks/useRequest';

import { RWListRules } from '@nextroll/ar-rollworks-list-builder';

import { IWorkflowAccountActionContactFilter } from 'types/roflWorkflowActions';

import { ValidationErrorType } from '../util/errors';
import { LookupType, RecordField } from './roflWorkflowActionProperty';
import { ActionPropertyValuesInput } from './propertyGeneration';


/**
* ActionMetadataPropertyLookupSelected contains the metadata for a selected lookup field for a workflow action property.
*/
interface ActionMetadataPropertyLookupSelected {
    // DisplayValue contains the user facing version of the value
    display_value: string;
}

/**
*   ActionMetadataPropertyOptionsLookup contains the metadata for a lookup of type "options"
*/
interface ActionMetadataPropertyOptionsLookup {
    // Selected describes the details of what value was selected in this single lookup.
    selected: ActionMetadataPropertyLookupSelected;
}

/**
*   ActionMetadataPropertySingleLookup contains the metadata for a lookup of type "single", which returns a single
*   entity that matches a "text" parameter passed to the "/:eid/properties/:property_name/values/single" endpoint.
*/
interface ActionMetadataPropertySingleLookup {
    // Text contains the "text" parameter provided by the user, such as the Marketo URL.
    text: string;
    // Selected describes the details of what value was selected in this single lookup.
    selected: ActionMetadataPropertyLookupSelected;
}

/**
* ActionMetadataPropertyLookup describes the metadata for a lookup related to a workflow action property.
*/
export interface ActionMetadataPropertyLookup {
    // Type determines how the UI should interact with this lookup property.
    type: LookupType;
    // SingleLookup contains the metadata for a single lookup when Type is WorkflowActionLookupTypeSingle
    single_lookup?: ActionMetadataPropertySingleLookup;

    // OptionsLookup contains the metadata for an options lookup when Type is WorkflowActionLookupTypeOptions
    options_lookup?: ActionMetadataPropertyOptionsLookup;
}

// ActionMetadataRecordField describes the metadata for a record property. We can think of these as "sub-fields"
// of the record property itself. It uses a separate struct to avoid a recursive definition.
export interface ActionMetadataRecordField {
    // Index contains the field order in the UI. It must be provided by the UI on save and will
    // be used on edit to re-render the form correctly.
    index: number;
    // Definition will contain the original field definition for this record property. This is set by the rofl-api.
    readonly definition?: RecordField;
    // Lookup may optionally contain the details of how a value was looked up and which value was selected for
    // this property. This may optionally be set by the UI.
    lookup?: ActionMetadataPropertyLookup;
}

/**
* ActionMetadataProperty describes the metadata for a top level workflow action property.
*/
export interface ActionMetadataProperty {
    // Lookup contains the metadata for the lookup used to produce the selected property value, if available.
    lookup?: ActionMetadataPropertyLookup;
    // fields contains the metadata for the sub-fields of the property,
    // if the property type is WorkflowActionPropertyTypeRecord
    fields?: {
        [name: string]: ActionMetadataRecordField;
    };
}

/**
*  ActionMetadata contains metadata the UI or validation utilities can use to make decisions or populate
*   labels and types.
*/
export interface ActionMetadata {
    // Properties contains the property level metadata for the workflow action configuration.
    properties: {
        [name: string]: ActionMetadataProperty;
    };
}

export const WORKFLOW_BASE = {
    CONTACTS: 'contact',
    ACCOUNTS: 'account',
} as const;
export const WORKFLOW_STATUS = {
    WORKFLOW_OFF: 'off',
    WORKFLOW_ON: 'on',
    WORKFLOW_DELETED: 'deleted',
} as const;

export const WORKFLOW_TRIGGER = {
    RECURRING: 'recurring',
    SCHEDULED: 'scheduled',
    PUSH: 'push',
} as const;

export const WORKFLOW_NODE = {
    TRIGGER: 'trigger',
    ACTION: 'action',
    ACTION_GROUP: 'action-group',
    IF_CONDITION: 'if-condition',
    IF_ELSE_CONDITION: 'if-else-condition',
} as const;

export const WORKFLOW_TRIGGER_CONTACT_LIST_STATUS = {
    SYNCING: 'syncing',
    STOPPED_SYNCING: 'stopped_syncing',
} as const;

export const WORKFLOW_NODE_VALIDATION_ISSUE_CODES = {
    CONTACT_LIST_STOPPED_SYNCING: 'contact_list_stopped_syncing',
    INVALID_INPUT: 'invalid_input',
    SUBSCRIPTION_FEATURE_MISSING: 'subscription_feature_missing',
    ENTITY_NOT_FOUND: 'entity_not_found',
    AUTHENTICATION_DISCONNECTED: 'authentication_disconnected',
    MISSING_PERMISSIONS: 'authentication_missing_permissions',
    FIELD_MISSING: 'field_missing',
    READ_ONLY_FIELD: 'read_only_field_for_record_operation',
    MISSING_REQUIRED_FIELD: 'missing_required_field_for_record_operation',
    FIELD_MISSING_IN_DESTINATION: 'field_missing_in_destination',
    REQUIRED_FIELD_MISSING_IN_DESTINATION: 'required_field_missing_in_destination',
    RULE_VALIDATION_ISSUE: 'rule_validation_issue',
    INSUFFICIENT_CONTACT_DISCOVERY_CREDITS: 'insufficient_contact_discovery_credits',
} as const;

export type WorkflowBaseType = typeof WORKFLOW_BASE[keyof typeof WORKFLOW_BASE];
export type WorkflowStatusType = typeof WORKFLOW_STATUS[keyof typeof WORKFLOW_STATUS];
export type WorkflowTriggerType = typeof WORKFLOW_TRIGGER[keyof typeof WORKFLOW_TRIGGER];
export type WorkflowNodeType = typeof WORKFLOW_NODE[keyof typeof WORKFLOW_NODE];
export type WorkflowTriggerContactListStatusType =
    typeof WORKFLOW_TRIGGER_CONTACT_LIST_STATUS[keyof typeof WORKFLOW_TRIGGER_CONTACT_LIST_STATUS];

export type NodeIssueCode =
    typeof WORKFLOW_NODE_VALIDATION_ISSUE_CODES[keyof typeof WORKFLOW_NODE_VALIDATION_ISSUE_CODES];

export type WorkflowNodeNextPointerType = string[] | string | null; // TBD

export type WorkflowTriggerResultsType =
    | { contacts: any[]; totalCount: number }
    | { accounts: any[]; totalCount: number }
    | null;

export interface IWorkflowSchemaTriggerExclusions {
    not_in_crm_accounts?: boolean;
    not_in_sales_marketing?: boolean;
    not_in_sales_marketing_since?: string;
    not_in_advertising_filter?: boolean;
    not_in_intent?: boolean;
    exclusions_types?: ('competitors' | 'coworkers' | 'customers')[];
    excluded_accounts?: string[];
}
export interface IWorkflowSchemaAccountConfig extends IWorkflowSchemaTriggerExclusions {
    list_rule?: RWListRules;
}
export interface IWorkflowSchemaTrigger {
    eid?: string;
    type?: WorkflowTriggerType;
    contact_list_eid?: string;
    include_current_entities_in_first_run?: boolean;
    entity_type?: WorkflowBaseType;
    reentry_after?: string;
    cron_expression?: string;
    next?: WorkflowNodeNextPointerType;
    rules?: RWListRules;

    // new data structure for account-based workflows
    config?: IWorkflowSchemaAccountConfig;
}

interface ActionConfigEntry {
    [key: string]:
        | string | string[] | number | boolean | ActionConfigEntry
        | { rules: IWorkflowAccountActionContactFilter[] }; // filtered contacts
}

export interface IWorkflowActionConfig {
    action_eid?: string;
    config?: ActionConfigEntry;
    metadata?: ActionMetadata;
}

export interface IPropertyError {
    property_name: string;
    error: ValidationErrorType;
    record_field?: string;
}
export interface IPropertyWarning {
    property_name: string;
    error: ValidationErrorType;
    record_field?: string;
}

export interface IBaseNodeIssue {
    node_eid: string;
    error?: NodeIssueCode;
    data?: any;
    property_errors?: IPropertyError[];
    property_warnings?: IPropertyWarning[];
}

export interface INodeIssue extends IBaseNodeIssue {
    warning?: NodeIssueCode;
}
export interface ITriggerIssue extends IBaseNodeIssue {
    warning?: NodeIssueCode;
}

export interface IWorkflowSchemaErrors {
    trigger_errors: ITriggerIssue[];
    action_errors: INodeIssue[];
}
export interface IWorkflowSchemaWarnings {
    trigger_warnings: ITriggerIssue[];
    action_warnings: INodeIssue[];
}

interface IWorkflowErrors {
    schema_errors: IWorkflowSchemaErrors;
}
interface IWorkflowWarnings {
    schema_warnings: IWorkflowSchemaWarnings;
}

export interface IWorkflowSchemaNode {
    eid: string;
    type: string;
    action_config?: IWorkflowActionConfig; // only in individual nodes or sub-nodes
    action_group_config?: { nodes?: string[] }; // only in group nodes
    next?: WorkflowNodeNextPointerType;
    error?: boolean;
    warnings?: IWorkflowWarnings;
    errors?: IWorkflowErrors;
}
export interface IWorkflowSchema {
    trigger: IWorkflowSchemaTrigger;
    nodes: { [id: string]: IWorkflowSchemaNode };
}

export interface IWorkflowStats {
    workflow_eid: string;
    sum_of_enrolled_entities: number;
    latest_enrollment_timestamp: string;
}

export interface IWorkflow extends Partial<IWorkflowStats> {
    advertisable_eid: string;
    eid: string;
    name: string;
    status: WorkflowStatusType;
    schema: IWorkflowSchema;
    entity_type?: WorkflowBaseType;
    created_at: string;
    created_by?: string;
    updated_at: string;
    updated_by: string;
    updated_by_name?: string;
    template_eid?: string;
    last_stopped_at?: string;
    cron_expression?: string;
    entities_enrolled_last_run: number;
    total_enrolled_entities: number;
    errors?: IWorkflowErrors;
    warnings?: IWorkflowWarnings;
}

export interface IWorkflowValidationResult {
    success: boolean;
    schema_errors: IWorkflowSchemaErrors;
    schema_warnings: IWorkflowSchemaWarnings;
}

export const TRAY_SERVICE = {
    HUBSPOT: 'hubspot',
    MARKETO: 'marketo',
    SALESFORCE: 'salesforce',
    ROLLWORKS: 'rollworks',
} as const;

// These can be used as the names for keys in maps
export const TRAY_SERVICE_CONST_NAME = {
    HUBSPOT: 'HUBSPOT',
    MARKETO: 'MARKETO',
    SALESFORCE: 'SALESFORCE',
    ROLLWORKS: 'ROLLWORKS',
} as const;

export type TrayServiceType = typeof TRAY_SERVICE[keyof typeof TRAY_SERVICE];
export type TrayServiceConstNameType = typeof TRAY_SERVICE_CONST_NAME[keyof typeof TRAY_SERVICE_CONST_NAME];

/**
 *  NodeDropDownSectionValue represents the internal drop down section values
 */
export interface NodeDropDownSectionValue {
    value: any;
    display_value: string;
}

export type NodeDropDownSectionDataFcn = ((getDataConfig?: {
    onError?: (requestError: RequestError) => boolean;
    searchParam?: string;
}) => Promise<NodeDropDownSectionValue[]>);


export interface NodeDropdownDefaultProps <T>{
    data?: T[];
    loading?: boolean;
    warning?: string;
    error?: React.ReactNode;

    placeholder?: string;

    // callbacks for rendering dropdown options
    renderSelected?: (option: T) => boolean;

    // callbacks for dropdown related events
    onSelect?: (option: T, selected?: boolean, selectedOptions?: T[]) => void;

    // determine how and when to refresh the dropdown content
    allowRefresh?: boolean;
    errorElement?: JSX.Element;
}

export interface NodeDropDownSectionProps {
    selected: any;
    selectedDisplayValue?: string;
    onSearch?: (search: string, option: NodeDropDownSectionValue) => boolean;
    onSelect: (
        option : NodeDropDownSectionValue, isOptionSelected?: boolean, selectedOptions?: NodeDropDownSectionValue[]
    ) => void;
    serviceTitle: string;
    entityTitle: string;
    // Fixed custom title for the dropdown section
    dropdownTitle?: string;
    allowRefresh: boolean;
    data: NodeDropDownSectionValue[] | NodeDropDownSectionDataFcn;
    propertyError?: IPropertyError;
    multiple?: boolean;
    // identifier used to determine if the field is valid in the isValid map
    identifier: string;
    // setter for a map to determine if field is valid
    setIsValid: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    suppressSearch?: boolean;
    placeholder?: string;
    // determines if the dropdown is disabled
    isDisabled?: boolean;
    actionPropertyValuesInput?: ActionPropertyValuesInput;
    hideLabel: boolean;
    shouldSearchBackend?: boolean;
}

export type ActionConfigValues = Record<string, any>;

export const SALESFORCE_ACTION_EID = 'UUIWLDKTN5F5ZMXVGZ8RFL';
export const HUBSPOT_ACTION_EID = 'WWQBNUA7NJATJP2PUO8RFL';
export const EMAIL_ACTION_EID = 'KMJW4GWNNFFJFM4GPO8RFL';

export const ACTION_EID_BY_SERVICE = {
    [TRAY_SERVICE.HUBSPOT]: HUBSPOT_ACTION_EID,
    [TRAY_SERVICE.SALESFORCE]: SALESFORCE_ACTION_EID,
} as const;

// possible CRM options
export type CrmOptionType = 'salesforce' | 'hubspot' | 'other';
